/** A session code is six characters, letters and digits. */
const CODE_PATTERN = /^[A-Z0-9]{6}$/;

/**
 * What a scanned QR code means to this app, or `null` if it means nothing.
 *
 * A camera sees whatever is in front of it, and a poster, a receipt and a
 * wifi code are all QR codes too. Everything that is not recognisably one of
 * ours is rejected rather than half-interpreted: the scanner keeps looking,
 * which is what somebody pointing a phone at the right thing expects.
 *
 * Two shapes are accepted — our own invite link, and a bare code, because a
 * code written on a whiteboard and photographed is still a code.
 */
export function sessionCodeFromScan(raw: string): string | null {
  const text = raw.trim();
  if (text.length === 0) return null;

  const bare = text.toUpperCase();
  if (CODE_PATTERN.test(bare)) return bare;

  try {
    const url = new URL(text);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    const code = url.searchParams.get('code')?.toUpperCase() ?? '';
    return CODE_PATTERN.test(code) ? code : null;
  } catch {
    // Not a URL at all.
    return null;
  }
}

interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string }[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

function detectorConstructor(): BarcodeDetectorConstructor | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as unknown as { BarcodeDetector?: BarcodeDetectorConstructor })
    .BarcodeDetector;
  return typeof candidate === 'function' ? candidate : null;
}

/**
 * Whether this browser can scan at all.
 *
 * Two separate capabilities, and both are needed: a camera the page may open,
 * and the browser's own barcode reader. The second is Chrome on Android and
 * little else — decoding frames in JavaScript instead would mean shipping a
 * decoder to every visitor for a feature most of them never open, so where
 * it is missing the app simply does not offer scanning. Typing six characters
 * is not a hardship; a button that opens a camera and then does nothing is.
 */
export function qrScanningSupported(): boolean {
  if (detectorConstructor() === null) return false;
  if (typeof navigator === 'undefined') return false;
  return typeof navigator.mediaDevices?.getUserMedia === 'function';
}

/** Builds a QR-only detector, or `null` where the browser has none. */
export async function createQrDetector(): Promise<BarcodeDetectorLike | null> {
  const Detector = detectorConstructor();
  if (!Detector) return null;
  try {
    // Asking first: a browser can carry the class and still not read QR.
    const formats = (await Detector.getSupportedFormats?.()) ?? ['qr_code'];
    if (!formats.includes('qr_code')) return null;
    return new Detector({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

/** The first thing in view that is one of our codes. */
export function firstSessionCode(results: { rawValue: string }[]): string | null {
  for (const result of results) {
    const code = sessionCodeFromScan(result.rawValue);
    if (code) return code;
  }
  return null;
}
